// src/components/ProtectedWithConsent.jsx
// Route wrapper that ensures user has accepted consent before accessing protected routes (like Home)
// This is a required legal gate - users cannot access Home without accepting consent
// OPTIMIZED: Cache-first, verify-later with timeout to prevent redirect loops

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { ConsentContext } from '../context/ConsentContext';
import { UserContext } from '../context/UserContext';
import api from '../utils/api';

const CONSENT_CHECK_TIMEOUT = 1500; // 1.5 seconds timeout

/**
 * Check if local consent cache indicates user has consent
 */
function hasLocalConsent() {
  try {
    const cached = JSON.parse(localStorage.getItem('consent_cached') || 'null');
    if (cached?.has_consent === true && cached?.consent_required !== true) return true;
  } catch {}
  if (localStorage.getItem('cookie_consent_given') === 'true') return true;
  // Optional cookie flag
  if (document.cookie.split('; ').some(c => c.startsWith('bloompal_consent=true'))) return true;
  return false;
}

/**
 * ProtectedWithConsent - Ensures authenticated users have accepted consent before accessing route
 * 
 * Flow (CACHE-FIRST):
 * 1. If local consent exists, render immediately and verify in background
 * 2. If local consent missing, do ONE server check (timeout 1500ms)
 * 3. Only redirect when backend definitively says consent_required=true OR has_consent=false
 * 4. Preserve "next" path when redirecting to prevent losing destination
 */
const ProtectedWithConsent = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken } = useContext(UserContext) || {};
  const { consent, loading: consentLoading } = useContext(ConsentContext) || {};
  const [status, setStatus] = useState('checking');
  const isAuthenticated = Boolean(authToken);
  const abortControllerRef = useRef(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    // Only check consent for authenticated users
    if (!isAuthenticated) {
      setStatus('done');
      return;
    }

    // Prevent duplicate checks
    if (checkedRef.current) return;
    checkedRef.current = true;

    const checkConsent = async () => {
      // Clear any previous abort controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Cache-first: if local consent exists, render immediately and verify in background
      if (hasLocalConsent()) {
        setStatus('done');
        
        // Verify in background (non-blocking)
        try {
          const res = await api.get('/api/consent/current/', {
            signal: abortController.signal,
            timeout: CONSENT_CHECK_TIMEOUT
          });
          
          if (abortController.signal.aborted) return;
          
          const data = res.data || {};
          // If backend definitively says no consent or consent required, clear cache and redirect
          if (data.has_consent === false || data.consent_required === true) {
            localStorage.removeItem('consent_cached');
            localStorage.removeItem('cookie_consent_given');
            const nextPath = location.pathname + location.search;
            navigate('/consent', { replace: true, state: { next: nextPath } });
          }
        } catch (err) {
          // Network error - keep local cache, don't redirect
          if (!abortController.signal.aborted) {
            console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
          }
        }
        return;
      }

      // No local consent: do ONE server check
      try {
        const res = await api.get('/api/consent/current/', {
          signal: abortController.signal,
          timeout: CONSENT_CHECK_TIMEOUT
        });

        if (abortController.signal.aborted) return;

        const data = res.data || {};
        
        if (data.has_consent === true && data.consent_required !== true) {
          // Backend says consent exists - hydrate cache and render
          localStorage.setItem('cookie_consent_given', 'true');
          localStorage.setItem('consent_cached', JSON.stringify({ ...data, has_consent: true, consent_required: false }));
          setStatus('done');
        } else {
          // Backend says no consent or consent required - redirect
          localStorage.removeItem('consent_cached');
          localStorage.removeItem('cookie_consent_given');
          const nextPath = location.pathname + location.search;
          navigate('/consent', { replace: true, state: { next: nextPath } });
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        
        // Network error or timeout - allow rendering (fail-open)
        // User can still interact, banner will show if needed
        console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
        setStatus('done');
      }
    };

    // Start checking
    checkConsent();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isAuthenticated, navigate, location.pathname, location.search]);

  // Render children immediately (non-blocking)
  // No spinner - cache-first means we render immediately if consent cached
  return <>{children}</>;
};

export default ProtectedWithConsent;

